import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isExplicitlyClearedUserValue } from "@/utils/sanitizeValue";

/**
 * One user edit of any attribute of a building, newest-first. `data.user_data`
 * is append-only, so every edit is its own row — `get_building_data_history`
 * returns the building-wide timeline (all attributes), unlike
 * `get_user_data_history` which is scoped to a single attribute. Machine
 * sources (EHR / extractor / IFC) are UPSERT snapshots with no history, so
 * user edits are the only per-change log available.
 */
export interface BuildingDataHistoryEntry {
  attributeKey: string;
  attributeValue: string | null;
  editedBy: number | null;
  editedByName: string | null;
  editedByAvatarUrl: string | null;
  editedByColor: string | null;
  createdAt: string;
}

/** Raw RPC row shape from `public.get_building_data_history`. */
interface HistoryRow {
  attribute_key: string;
  attribute_value: string | null;
  edited_by: number | null;
  edited_by_name: string | null;
  edited_by_avatar_url: string | null;
  edited_by_color: string | null;
  created_at: string;
}

export const BUILDING_DATA_HISTORY_QUERY_KEY = "building-data-history" as const;

/**
 * Lazily fetches the building-wide user-edit history. Only runs while
 * `enabled` (the history modal is open) and the org/building ids are present —
 * the caller already gates on org readiness before rendering the page, so a
 * present `orgId` is a ready org.
 */
export function useBuildingDataHistory(
  buildingId: string | undefined,
  orgId: string | undefined,
  enabled: boolean
) {
  return useQuery<BuildingDataHistoryEntry[]>({
    queryKey: [BUILDING_DATA_HISTORY_QUERY_KEY, buildingId, orgId],
    enabled: enabled && !!buildingId && !!orgId,
    queryFn: async (): Promise<BuildingDataHistoryEntry[]> => {
      if (!buildingId || !orgId) return [];
      const { data, error } = await supabase.rpc(
        "get_building_data_history" as never,
        {
          p_org_id: orgId,
          p_building_id: parseInt(buildingId, 10),
        } as never
      );
      if (error) {
        console.warn("[building-data-history] query failed", error);
        throw error;
      }
      const rows = (data as unknown as HistoryRow[]) ?? [];
      return rows.map((r) => ({
        attributeKey: r.attribute_key,
        // The rejection marker is bookkeeping, never something a user typed —
        // the timeline renders it as a cleared value, like `useAttributeHistory`.
        attributeValue: isExplicitlyClearedUserValue(r.attribute_value) ? null : r.attribute_value,
        editedBy: r.edited_by,
        editedByName: r.edited_by_name,
        editedByAvatarUrl: r.edited_by_avatar_url,
        editedByColor: r.edited_by_color,
        createdAt: r.created_at,
      }));
    },
  });
}
